import React, { FunctionComponent, useState, useEffect } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
    TextField,
    Grid,
    Typography,
    LinearProgress,
    Box,
    Tooltip,
    FormControl,
} from '@material-ui/core';
import prettyBytes from 'pretty-bytes';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CloudUploadOutlined from '@material-ui/icons/CloudUploadOutlined';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import { MESSAGES, APPLICATION_TYPES } from '../../store/app.constants';
import { Api, API_CONFIGURATIONS } from '../../store/services/services';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { DropDownField } from '../../store/modules/application/types';

type UploadedFileProps = {
    file: File;
    name: string;
    type: string;
    activeIndex: number;
    fileIndex: number;
    nextUpload: Function;
    isUploading: boolean;
    filesCount: number;
    stopUpload: Function;
    removeFile: Function;
    isDisabled: boolean;
    licenseId: string;
    personId: string;
    citationId: string;
    getAuthenticationToken: Function;
    getFileList: Function;
    setDocumentTypeForParent: Function;
    setFileNameForParent: Function;
    toggleFileRequestTrigger?: Function;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingTop: '10px',
            minWidth: '100%',
            '& .MuiOutlinedInput-input': {
                padding: theme.spacing(1),
            },
        },
        container: {
            alignItems: 'center',
        },
        fileSize: {
            color: '#6E7788',
            textAlign: 'center',
        },
        indicator: {
            margin: '-6px 10px 8px',
        },
        indicators: {
            paddingTop: '10px !important',
        },
        applicationTypeInput: {
            padding: '2.1px 4px !important',
        },
        applicationTypePaper: {
            width: '180px',
        },
    }),
);

const UploadedFile: FunctionComponent<UploadedFileProps> = (props) => {
    const {
        file,
        name,
        type,
        fileIndex,
        activeIndex,
        nextUpload,
        isUploading,
        filesCount,
        stopUpload,
        removeFile,
        isDisabled,
        licenseId,
        personId,
        citationId,
        getAuthenticationToken,
        getFileList: getFiles,
        setDocumentTypeForParent,
        setFileNameForParent,
        toggleFileRequestTrigger,
    } = props;

    const recordId = licenseId || personId || citationId;
    const licenseFileUploadUrl = `${API_CONFIGURATIONS.BASE_URL}/licenses/${licenseId}/documents`;
    const personFileUploadUrl = `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/documents`;
    const citationFileUploadUrl = `${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/documents`;
    const url = licenseId ? licenseFileUploadUrl : personId ? personFileUploadUrl : citationFileUploadUrl;

    const classes = useStyles();

    const [percentage, setPercentage] = useState<number>(0);
    const [status, setStatus] = useState<string>('');
    const [fileExtension] = useState<string>(`.${name.split(/\.(?=[^.]+$)/)[1]}`);
    const [applicationType, setApplicationType] = useState<DropDownField | null>(null);

    const makeFile = (file: File) => {
        let data = new FormData();
        let newFile = new File([file], name, {
            type: file.type,
        });
        data.append('files', newFile);
        data.append('documenttype', type);
        return data;
    };

    const data = makeFile(file);

    const options = {
        onUploadProgress: (progressEvent: any) => {
            const { loaded, total } = progressEvent;
            let mPercent = (loaded / total) * 100;
            if (mPercent <= 100 && percentage < mPercent) {
                setPercentage(mPercent);
            }
        },
    };

    useEffect(() => {
        if (isUploading && activeIndex === fileIndex) {
            getAuthenticationToken((token: string) => {
                Api.uploadFile(token, data, recordId, options, url)
                    .then(() => {
                        setStatus(MESSAGES.FILE_UPLOAD_SUCCESS);
                        getFiles(recordId);

                        toggleFileRequestTrigger && toggleFileRequestTrigger(true);
                    })
                    .catch(() => setStatus(MESSAGES.FILE_UPLOAD_FAIL))
                    .finally(() => {
                        if (activeIndex < filesCount) {
                            nextUpload(fileIndex + 1);
                        }
                    });
            });
        }
        if (filesCount - 1 < activeIndex) {
            stopUpload();
        }

        // eslint-disable-next-line
    }, [isUploading, activeIndex, getAuthenticationToken, setStatus]);

    useEffect(() => {
        const selectedApplicationType = Object.entries(APPLICATION_TYPES).find(
            (applicationtype) => applicationtype[1] === type,
        );
        selectedApplicationType &&
            setApplicationType({ label: selectedApplicationType[0], value: selectedApplicationType[1] });
    }, [type]);

    const handleStatusChange = (data: DropDownField | null) => {
        setDocumentTypeForParent(fileIndex, data);
    };

    return (
        <Grid className={classes.container} container spacing={1}>
            <Grid item xs={5}>
                <TextField
                    disabled={status === 'success' || isDisabled}
                    classes={{ root: classes.root }}
                    variant="outlined"
                    value={name.split(/\.(?=[^.]+$)/)[0]}
                    onChange={(e: any) => {
                        setFileNameForParent(fileIndex, e.target.value, fileExtension);
                    }}
                />
            </Grid>
            <Grid className={classes.fileSize} item xs={3}>
                <FormControl variant="outlined" className={classes.root}>
                    <AutoCompleteSelect
                        id="demo-customized-select"
                        data={Object.entries(APPLICATION_TYPES).map((type) => ({
                            label: type[0],
                            value: type[1],
                        }))}
                        onChange={handleStatusChange}
                        inputSize={'small'}
                        disableClearable={true}
                        value={applicationType}
                        classes={{
                            input: classes.applicationTypeInput,
                            paper: classes.applicationTypePaper,
                        }}
                        variant={'outlined'}
                        ariaLabel="Document Type"
                        disabled={status === 'success' || isDisabled}
                    />
                </FormControl>
            </Grid>
            <Grid className={classes.fileSize} item xs={2}>
                <Typography variant="caption">{prettyBytes(file.size)}</Typography>
            </Grid>
            <Grid className={classes.indicators} item xs={1}>
                {status === '' && !isUploading && (
                    <Tooltip title="Remove" placement="top">
                        <Box
                            onClick={() => {
                                removeFile(fileIndex);
                            }}
                        >
                            <CancelOutlinedIcon />
                        </Box>
                    </Tooltip>
                )}
                {status === '' && isUploading && (
                    <Tooltip title="Uploading" placement="top">
                        <Box>
                            <CloudUploadOutlined />
                        </Box>
                    </Tooltip>
                )}
                {status === 'success' && (
                    <Tooltip title="Completed" placement="top">
                        <Box>
                            <CheckCircleOutlinedIcon color="primary" />
                        </Box>
                    </Tooltip>
                )}
                {status === 'fail' && (
                    <Tooltip title="Failed" placement="top">
                        <Box>
                            <ErrorOutlineOutlinedIcon color="error" />
                        </Box>
                    </Tooltip>
                )}
            </Grid>
            <Grid item xs={1}>
                {status === 'fail' && (
                    <Box className={classes.indicator}>
                        <Typography color="error" variant="caption">
                            Upload failed
                        </Typography>
                    </Box>
                )}
            </Grid>
            {isUploading && fileIndex === activeIndex && (
                <Grid item xs={12}>
                    <LinearProgress value={percentage} variant="determinate" />
                </Grid>
            )}
        </Grid>
    );
};

export default UploadedFile;
